import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { Divisa } from "../models/Divisa";
import { ComisionRecargo } from "../models/Dte/ComisionRecargo";
import { DetalleDte } from "../models/Dte/DetalleDte";
import { ExportacionTransporteDte } from "../models/Dte/exportacionDte";
import { FormatoDte } from "../models/Dte/FormatoDte";
import { InvoiceType } from "../models/Dte/invoiceType.enum";
import { OptionsReferenceDteEmitido } from "../models/Dte/OptionsReferenceDteEmitido";
import { OptionsTransportDteEmitido } from "../models/Dte/OptionsTransportDteEmitido";
import { ReferenciaDteEmitido } from "../models/Dte/ReferenciaDteEmitido";
import { TotalDteEmitido } from "../models/Dte/TotalDteEmitido";
import { NotaCredito } from "../models/NotaCredito";
import { NotaDebito } from "../models/NotaDebito";
import { Proveedor } from "../models/Proveedor/Proveedor";
import { Receptor } from "../models/Receptor/Receptor";
import { InvoicingButton } from "../shared/models/InvoicingButton.enum";

@Injectable({ providedIn: "root" })
export class FacturadorIndividualStateService {
	private detalles: DetalleDte[] = [];
	private comisiones: ComisionRecargo[] = [];
	private maximoDetalles = 5;
	private readonly maximoComisiones = 20;
	private lineasDetalles = 0;
	private lineasComisiones = 0;
	private detallesInvalidos = false;
	private comisionesInvalidas = false;

	private readonly detallesSubject = new BehaviorSubject<DetalleDte[]>([]);
	private readonly comisionesSubject = new BehaviorSubject<ComisionRecargo[]>([]);
	private readonly totalesSubject = new BehaviorSubject<TotalDteEmitido>(new TotalDteEmitido());
	private readonly clienteIdSubject = new BehaviorSubject<string>("");
	private readonly referenciasSubject = new BehaviorSubject<OptionsReferenceDteEmitido>(new OptionsReferenceDteEmitido());
	private readonly transporteSubject = new BehaviorSubject<OptionsTransportDteEmitido>(new OptionsTransportDteEmitido());
	private readonly clienteSubject = new BehaviorSubject<Receptor | null>(null);
	private readonly proveedorSubject = new BehaviorSubject<Proveedor | null>(null);
	private readonly formatoSubject = new BehaviorSubject<number>(10);
	private readonly formatoListaSubject = new BehaviorSubject<FormatoDte[]>([]);
	private readonly formatoLoadingSubject = new BehaviorSubject<boolean>(false);
	private readonly tamanioInvalidoSubject = new BehaviorSubject<boolean>(false);
	private readonly enviadoSubject = new BehaviorSubject<boolean>(false);
	private readonly divisasSubject = new BehaviorSubject<Divisa[]>([]);
	private readonly limpiarListaSubject = new BehaviorSubject<boolean>(false);
	private readonly tipoDteSubject = new BehaviorSubject<InvoiceType>(InvoiceType.Factura);
	private readonly detalleConImpuestoSubject = new BehaviorSubject<boolean>(true);
	private readonly exportacionSubject = new BehaviorSubject<ExportacionTransporteDte>(new ExportacionTransporteDte());
	private readonly accionesSubject = new Subject<InvoicingButton>();
	private readonly codigosAduanaLoadingSubject = new BehaviorSubject<boolean>(false);
	private readonly clienteLoadingSubject = new BehaviorSubject<boolean>(false);
	private readonly reiniciarClienteSubject = new BehaviorSubject<boolean>(false);
	private readonly clienteDescargadoSubject = new BehaviorSubject<boolean>(true);
	private readonly referenciasGuiasSubject = new BehaviorSubject<ReferenciaDteEmitido[]>([]);
	private readonly detallesGuiasSubject = new BehaviorSubject<DetalleDte[]>([]);
	private readonly agregarProductoSubject = new BehaviorSubject<DetalleDte | undefined>(undefined);
	private readonly agregarDocumentoSubject = new BehaviorSubject<DetalleDte | undefined>(undefined);
	private readonly foliosSubject = new BehaviorSubject<[string, number]>(["", 0]);
	private readonly notaDebitoSubject = new BehaviorSubject<NotaDebito>(new NotaDebito());
	private readonly notaCreditoSubject = new BehaviorSubject<NotaCredito>(new NotaCredito());

	readonly detalles$: Observable<DetalleDte[]> = this.detallesSubject.asObservable();
	readonly comisiones$: Observable<ComisionRecargo[]> = this.comisionesSubject.asObservable();
	readonly totales$: Observable<TotalDteEmitido> = this.totalesSubject.asObservable();
	readonly clienteId$: Observable<string> = this.clienteIdSubject.asObservable();
	readonly referencias$: Observable<OptionsReferenceDteEmitido> = this.referenciasSubject.asObservable();
	readonly transporte$: Observable<OptionsTransportDteEmitido> = this.transporteSubject.asObservable();
	readonly cliente$: Observable<Receptor | null> = this.clienteSubject.asObservable();
	readonly proveedor$: Observable<Proveedor | null> = this.proveedorSubject.asObservable();
	readonly formato$: Observable<number> = this.formatoSubject.asObservable();
	readonly formatos$: Observable<FormatoDte[]> = this.formatoListaSubject.asObservable();
	readonly formatoLoading$: Observable<boolean> = this.formatoLoadingSubject.asObservable();
	readonly tamanioInvalido$: Observable<boolean> = this.tamanioInvalidoSubject.asObservable();
	readonly enviado$: Observable<boolean> = this.enviadoSubject.asObservable();
	readonly divisas$: Observable<Divisa[]> = this.divisasSubject.asObservable();
	readonly limpiarLista$: Observable<boolean> = this.limpiarListaSubject.asObservable();
	readonly tipoDte$: Observable<InvoiceType> = this.tipoDteSubject.asObservable();
	readonly detalleConImpuesto$: Observable<boolean> = this.detalleConImpuestoSubject.asObservable();
	readonly exportacion$: Observable<ExportacionTransporteDte> = this.exportacionSubject.asObservable();
	readonly acciones$: Observable<InvoicingButton> = this.accionesSubject.asObservable();
	readonly codigosAduanaLoading$: Observable<boolean> = this.codigosAduanaLoadingSubject.asObservable();
	readonly clienteLoading$: Observable<boolean> = this.clienteLoadingSubject.asObservable();
	readonly reiniciarCliente$: Observable<boolean> = this.reiniciarClienteSubject.asObservable();
	readonly clienteDescargado$: Observable<boolean> = this.clienteDescargadoSubject.asObservable();
	readonly referenciasGuias$: Observable<ReferenciaDteEmitido[]> = this.referenciasGuiasSubject.asObservable();
	readonly detallesGuias$: Observable<DetalleDte[]> = this.detallesGuiasSubject.asObservable();
	readonly agregarProducto$: Observable<DetalleDte | undefined> = this.agregarProductoSubject.asObservable();
	readonly agregarDocumento$: Observable<DetalleDte | undefined> = this.agregarDocumentoSubject.asObservable();
	readonly folios$: Observable<[string, number]> = this.foliosSubject.asObservable();
	readonly notaDebito$: Observable<NotaDebito> = this.notaDebitoSubject.asObservable();
	readonly notaCredito$: Observable<NotaCredito> = this.notaCreditoSubject.asObservable();

	selectedClient: Receptor | undefined;
	selectedProveedor: Proveedor | undefined;
	currentInvoiceType: InvoiceType = InvoiceType.Factura;

	constructor() {
		this.agregarDetalle(this.crearDetalleVacio());
		this.agregarComision(this.crearComisionVacia());
	}

	get invalidSize(): boolean {
		return this.detallesInvalidos || this.comisionesInvalidas;
	}

	get currentSize(): number {
		return Math.max(this.lineasDetalles, this.lineasComisiones);
	}

	clearAllDetails(): void {
		this.detalles = [];
		this.recalcularDetalles();
		this.detallesSubject.next([]);
	}

	setList(list: DetalleDte[]): void {
		this.detalles = [...(list || [])];
		this.publicarDetalles();
	}

	setComisionList(list: ComisionRecargo[]): void {
		this.comisiones = [...(list || [])];
		this.publicarComisiones();
	}

	agregarDetalle(item: DetalleDte): void {
		this.recalcularDetalles();
		if (this.detallesInvalidos) return;
		this.detalles = [...this.detalles, item];
		this.publicarDetalles();
	}

	agregarComision(item: ComisionRecargo): void {
		this.recalcularComisiones();
		if (this.comisionesInvalidas) return;
		this.comisiones = [...this.comisiones, item];
		this.publicarComisiones();
	}

	agregarNuevoDetalle(item?: DetalleDte): void {
		this.recalcularDetalles();
		if (this.detallesInvalidos) return;
		const nuevo = item || new DetalleDte();
		const vacio = this.indiceDetalleVacio();
		if (vacio >= 0 && (nuevo.relatedProductId || nuevo.nombre)) {
			this.detalles = this.detalles.map((actual, index) => (index === vacio ? nuevo : actual));
		} else {
			this.detalles = [...this.detalles, nuevo];
		}
		this.publicarDetalles();
	}

	agregarNuevaComision(): void {
		this.agregarComision(new ComisionRecargo());
	}

	limpiarPrimerDetalleVacio(): void {
		const item = this.detalles[0];
		if (this.esDetalleVacio(item)) {
			this.detalles = [];
			this.publicarDetalles();
		}
	}

	indiceDetalleVacio(): number {
		return this.detalles.findIndex((item) => this.esDetalleVacio(item));
	}

	actualizarDetalle(item: DetalleDte): void {
		const index = this.detalles.indexOf(item);
		if (index < 0) return;
		this.detalles = this.detalles.map((actual, itemIndex) => (itemIndex === index ? item : actual));
		this.publicarDetalles();
	}

	actualizarComision(item: ComisionRecargo): void {
		const index = this.comisiones.indexOf(item);
		if (index < 0) return;
		this.comisiones = this.comisiones.map((actual, itemIndex) => (itemIndex === index ? item : actual));
		this.publicarComisiones();
	}

	eliminarDetalle(index: number): void {
		this.detalles = this.detalles.filter((_, itemIndex) => itemIndex !== index);
		this.publicarDetalles();
	}

	eliminarComision(index: number): void {
		this.comisiones = this.comisiones.filter((_, itemIndex) => itemIndex !== index);
		this.publicarComisiones();
	}

	limpiarDetallesSinCantidad(): void {
		this.detalles = this.detalles.filter((item) => item.cantidad > 0);
		this.publicarDetalles();
	}

	limpiarComisionesSinCantidad(): void {
		this.comisiones = this.comisiones.filter((item) => item.cantidad > 0);
		this.publicarComisiones();
	}

	contarLineasDetalles(list: DetalleDte[]): number {
		return (list || []).reduce((total, item) => total + (item.descripcion ? 2 : 1), 0);
	}

	contarLineasComisiones(list: ComisionRecargo[]): number {
		return (list || []).length;
	}

	setListSnapshotValidation(): void {
		this.recalcularDetalles();
	}

	setCommissionSnapshotValidation(): void {
		this.recalcularComisiones();
	}

	updateClient(rut: string, client: Receptor | undefined): void {
		this.clienteIdSubject.next(rut);
		this.clienteDescargadoSubject.next(false);
		this.selectedClient = client;
		this.clienteSubject.next(client || null);
		this.selectedProveedor = undefined;
		this.proveedorSubject.next(null);
	}

	updateProveedor(rut: string, proveedor: Proveedor | undefined): void {
		this.clienteIdSubject.next(rut);
		this.clienteDescargadoSubject.next(false);
		this.selectedProveedor = proveedor;
		this.proveedorSubject.next(proveedor || null);
		this.selectedClient = undefined;
		this.clienteSubject.next(null);
	}

	updateOptionsTransport(options: OptionsTransportDteEmitido): void {
		this.transporteSubject.next(options);
	}

	updateOptionsReferences(options: OptionsReferenceDteEmitido): void {
		this.referenciasSubject.next(options);
	}

	updateTotales(totales: TotalDteEmitido): void {
		this.maximoDetalles = totales.formato?.size || 0;
		this.formatoSubject.next(this.maximoDetalles);
		this.totalesSubject.next(totales);
		this.recalcularDetalles();
	}

	updateFormatoDte(formatos: FormatoDte[]): void {
		this.maximoDetalles = formatos?.[0]?.maxLineas || 0;
		this.formatoSubject.next(this.maximoDetalles);
		this.formatoListaSubject.next([...(formatos || [])]);
		this.recalcularDetalles();
	}

	updateDivisas(divisas: Divisa[]): void {
		this.divisasSubject.next([...(divisas || [])]);
	}

	setCurrentInvoiceType(type: InvoiceType): void {
		this.currentInvoiceType = type;
		this.tipoDteSubject.next(type);
	}

	updateExportacionTransporte(exportacion: ExportacionTransporteDte): void {
		this.exportacionSubject.next(exportacion);
	}

	setDetailWithTaxInOptions(value: boolean): void {
		this.detalleConImpuestoSubject.next(value);
	}

	setLoadingFormatos(value: boolean): void {
		this.formatoLoadingSubject.next(value);
	}

	setLoadingCliente(value: boolean): void {
		this.clienteLoadingSubject.next(value);
	}

	setLoadingCodigosAduana(value: boolean): void {
		this.codigosAduanaLoadingSubject.next(value);
	}

	marcarEnviado(value: boolean): void {
		this.enviadoSubject.next(value);
	}

	enviarAccion(action: InvoicingButton): void {
		this.accionesSubject.next(action);
	}

	publicarFolios(info: [string, number]): void {
		this.foliosSubject.next(info);
	}

	publicarReferenciasGuias(references: ReferenciaDteEmitido[]): void {
		this.referenciasGuiasSubject.next([...(references || [])]);
	}

	publicarDetallesGuias(details: DetalleDte[]): void {
		this.detallesGuiasSubject.next([...(details || [])]);
	}

	publicarProducto(detail: DetalleDte | undefined): void {
		this.agregarProductoSubject.next(detail);
	}

	publicarDocumento(detail: DetalleDte | undefined): void {
		this.agregarDocumentoSubject.next(detail);
	}

	resetElements(): void {
		this.setList([]);
		this.agregarDetalle(this.crearDetalleVacio());
		this.setComisionList([]);
		this.agregarComision(this.crearComisionVacia());
		this.updateClient("", undefined);
		this.marcarEnviado(true);
		this.selectedClient = undefined;
		this.clienteSubject.next(null);
		this.selectedProveedor = undefined;
		this.proveedorSubject.next(null);
		this.clienteDescargadoSubject.next(true);
		this.detallesGuiasSubject.next([]);
		this.agregarProductoSubject.next(undefined);
		this.agregarDocumentoSubject.next(undefined);
		this.exportacionSubject.next(new ExportacionTransporteDte());
	}

	private publicarDetalles(): void {
		this.recalcularDetalles();
		this.detallesSubject.next([...this.detalles]);
	}

	private publicarComisiones(): void {
		this.recalcularComisiones();
		this.comisionesSubject.next([...this.comisiones]);
	}

	private recalcularDetalles(): void {
		this.lineasDetalles = this.contarLineasDetalles(this.detalles);
		this.detallesInvalidos = this.lineasDetalles > this.maximoDetalles;
		this.publicarTamanio();
	}

	private recalcularComisiones(): void {
		this.lineasComisiones = this.contarLineasComisiones(this.comisiones);
		this.comisionesInvalidas = this.lineasComisiones > this.maximoComisiones;
		this.publicarTamanio();
	}

	private publicarTamanio(): void {
		this.tamanioInvalidoSubject.next(this.invalidSize);
	}

	private esDetalleVacio(item?: DetalleDte): boolean {
		return !!item && (!item.cantidad || !item.precioUnitario);
	}

	private crearDetalleVacio(): DetalleDte {
		const detalle = new DetalleDte();
		detalle.precioUnitario = 0;
		detalle.cantidad = 0;
		detalle.esExento = false;
		detalle.itemId = "";
		detalle.descripcion = "";
		detalle.calcSubTotal();
		return detalle;
	}

	private crearComisionVacia(): ComisionRecargo {
		const comision = new ComisionRecargo();
		comision.neto = 0;
		comision.cantidad = 0;
		comision.exento = 0;
		comision.tasa = 0;
		comision.glosa = "";
		comision.tipoId = "";
		comision.tipoNombre = "";
		comision.tipoSigla = "";
		return comision;
	}
}
